import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatNumber } from '@/lib/currency.utils';
import {ReitDividendPayment} from "@/@types/reit.models.ts";

export interface REITDividendHistoryTableProps {
  payments: ReitDividendPayment[];
  onDelete: (id: string) => void;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function REITDividendHistoryTable({ payments, onDelete }: REITDividendHistoryTableProps) {
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const sorted = [...payments].sort(
    (a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
  );

  const handleConfirmDelete = () => {
    if (pendingDeleteId) {
      onDelete(pendingDeleteId);
      setPendingDeleteId(null);
    }
  };

  return (
    <>
      <div className="overflow-x-auto w-full">
        {sorted.length === 0 ? (
          <div
            className="flex items-center justify-center py-12 text-muted-foreground text-sm text-center px-4"
            data-testid="empty-state"
          >
            No dividend payments recorded yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Payment Date</TableHead>
                <TableHead className="text-right">Amount / Unit</TableHead>
                <TableHead className="text-right">Units Held</TableHead>
                <TableHead className="text-right">Total Amount</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{formatDate(payment.payment_date)}</TableCell>
                  <TableCell className="text-right">{formatNumber(payment.amount_per_unit, 4)}</TableCell>
                  <TableCell className="text-right">{formatNumber(payment.units_held_at_payment, 2)}</TableCell>
                  <TableCell className="text-right">{formatNumber(payment.total_amount)}</TableCell>
                  <TableCell>{payment.currency}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => setPendingDeleteId(payment.id)}
                      aria-label={`Delete payment from ${formatDate(payment.payment_date)}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={!!pendingDeleteId} onOpenChange={(open) => !open && setPendingDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Dividend Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this dividend payment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
